import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem("jumlaty_cart");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Flying-to-cart animation state
  // { id, from: {x,y}, color, label } — null when nothing is flying
  const [flying, setFlying] = useState(null);
  // Bump trigger for the cart icon to "pulse" when an item lands
  const [cartBump, setCartBump] = useState(0);

  useEffect(() => {
    localStorage.setItem("jumlaty_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1) => {
    const stock = Number(product?.stock ?? Infinity);
    setItems((prev) => {
      const existing = prev.find((it) => it.product_id === product.id);
      const currentQty = existing ? existing.quantity : 0;
      // Cap added quantity so currentQty + added never exceeds stock
      const allowedAdd = Math.max(0, Math.min(quantity, stock - currentQty));
      if (allowedAdd <= 0) return prev;
      if (existing) {
        return prev.map((it) =>
          it.product_id === product.id
            ? { ...it, quantity: it.quantity + allowedAdd }
            : it
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          name_ar: product.name_ar,
          price: product.price,
          color: product.color,
          // Persist image refs so the cart can show the actual product
          // picture (was a known bug — cart only showed text).
          image_path: product.image_path || null,
          image_path2: product.image_path2 || null,
          quantity: allowedAdd,
        },
      ];
    });
  };

  const triggerFly = useCallback((fromRect, product) => {
    if (!fromRect) return;
    setFlying({
      id: Date.now() + Math.random(),
      from: {
        x: fromRect.left + fromRect.width / 2,
        y: fromRect.top + fromRect.height / 2,
      },
      color: product?.color || "#E85D45",
      label: product?.name_ar?.slice(0, 1) || "+",
    });
  }, []);

  const onFlyEnd = useCallback(() => {
    setFlying(null);
    setCartBump((b) => b + 1);
  }, []);

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    setItems((prev) =>
      prev.map((it) => (it.product_id === productId ? { ...it, quantity } : it))
    );
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((it) => it.product_id !== productId));
  };

  const clearCart = () => setItems([]);

  // Backfill image fields on items added before image_path was stored —
  // called from Cart.jsx after fetching /products. imgMap = { [id]: {image_path, image_path2, color} }
  const hydrateImages = (imgMap) => {
    setItems((prev) =>
      prev.map((it) => {
        const meta = imgMap?.[it.product_id];
        if (!meta) return it;
        if (it.image_path || it.image_path2) return it;
        return {
          ...it,
          image_path: meta.image_path || null,
          image_path2: meta.image_path2 || null,
          color: it.color || meta.color,
        };
      }),
    );
  };

  const totalQuantity = items.reduce((sum, it) => sum + it.quantity, 0);
  const totalPrice = items.reduce((sum, it) => sum + it.price * it.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        hydrateImages,
        totalQuantity,
        totalPrice,
        flying,
        triggerFly,
        onFlyEnd,
        cartBump,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
